import React from 'react';

import { Trans } from '@lingui/macro';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Icon from '@mui/icons-material/SettingsEthernet';
import makeStyles from '@mui/styles/makeStyles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import YouTubeIcon from '@mui/icons-material/YouTube';

import * as S from '../../Sources/Network';
import Checkbox from '../../../../misc/Checkbox';
import Password from '../../../../misc/Password';

const useStyles = makeStyles(() => ({
	youtubeButton: {
		height: '56px',
		minWidth: '56px',
		color: 'rgba(255,255,255,0.5)',
		borderColor: 'rgba(128,128,128,0.35)',
		'&:hover': {
			borderColor: '#FF0000',
			color: '#FF0000',
			backgroundColor: 'transparent',
		},
	},
}));

// ── yt-dlp helpers (shared with Edit/Sources/Network) ──────────────────────
const _runtimeCfg = window.__RESTREAMER_CONFIG__ || {};
const STREAM_SERVICE_BASE = _runtimeCfg.YTDLP_URL
	? _runtimeCfg.YTDLP_URL.replace(/\/$/, '') + '/stream/'
	: '/yt-stream/';
const TITLES_SERVICE_BASE = _runtimeCfg.YTDLP_TITLES_URL
	? _runtimeCfg.YTDLP_TITLES_URL.replace(/\/$/, '') + '/info/'
	: '/yt-titles/';

const extractYouTubeVideoId = (url) => {
	if (!url) return '';
	const trimmed = url.trim();
	const patterns = [
		/[?&]v=([a-zA-Z0-9_-]{11})(?:[&?/]|$)/,
		/youtu\.be\/([a-zA-Z0-9_-]{11})(?:[?&/]|$)/,
		/\/(?:live|embed|shorts|v)\/([a-zA-Z0-9_-]{11})(?:[?&/]|$)/,
	];
	for (const pattern of patterns) {
		const match = trimmed.match(pattern);
		if (match) return match[1];
	}
	if (/^[a-zA-Z0-9_-]{11}$/.test(trimmed)) return trimmed;
	return '';
};

const initSettings = (initialSettings, config) => {
	const settings = {
		...S.func.initSettings(initialSettings, config),
		mode: 'pull',
	};

	return settings;
};

function Source(props) {
	const classes = useStyles();
	const config = S.func.initConfig(props.config);
	const settings = initSettings(props.settings, config);
	const skills = S.func.initSkills(props.skills);

	const [extractorLoading, setExtractorLoading] = React.useState(false);
	const [extractorError, setExtractorError]     = React.useState('');

	const detectedYouTubeId = extractYouTubeVideoId(settings.address);

	const handleChange = (newSettings) => {
		newSettings = newSettings || settings;
		props.onChange(S.id, newSettings, S.func.createInputs(newSettings, config, skills), S.func.isValidURL(newSettings.address));
	};

	const update = (protocol, what) => (event) => {
		const value = event.target.value;
		const newSettings = settings;
		if (protocol === 'rtsp') {
			if (['udp'].includes(what)) {
				newSettings.rtsp[what] = !newSettings.rtsp[what];
			}
		} else {
			newSettings[what] = value;
		}
		handleChange(newSettings);
	};

	const handleExtract = async () => {
		setExtractorError('');
		const videoId = extractYouTubeVideoId(settings.address);
		if (!videoId) {
			setExtractorError('No YouTube URL detected.');
			return;
		}
		setExtractorLoading(true);
		// AbortController con 90s — yt-dlp puede tardar 20-30s en extraer la URL
		const controller = new AbortController();
		const timeoutId  = setTimeout(() => controller.abort(), 90000);
		try {
			const response = await fetch(STREAM_SERVICE_BASE + videoId, { signal: controller.signal });
			clearTimeout(timeoutId);
			if (!response.ok) throw new Error('HTTP ' + response.status);
			const data = await response.json();
			if (data && data.stream_url) {
				const newSettings = { ...settings, address: data.stream_url };
				handleChange(newSettings);
				setExtractorError('');
					// Store the YouTube ID in the wizard metadata via onYoutubeMetadata
					if (videoId && typeof props.onYoutubeMetadata === 'function') {
						try {
							props.onYoutubeMetadata(videoId);
						} catch (e) {}
					}
			} else {
				setExtractorError('No stream_url found in service response.');
			}
		} catch (e) {
			clearTimeout(timeoutId);
			if (e.name === 'AbortError') {
				setExtractorError('Timeout: the extractor took too long. Try again.');
			} else {
				setExtractorError('Error: ' + e.message);
			}
		} finally {
			setExtractorLoading(false);
		}
	};

	React.useEffect(() => {
		handleChange();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const protocol = S.func.getProtocolClass(settings.address);

	return (
		<React.Fragment>
			<Grid item xs={12}>
				<Typography>
					<Trans>Enter the address of your network source:</Trans>
				</Typography>
			</Grid>
			<Grid item xs={12}>
				<Grid container spacing={1} alignItems="flex-start">
					<Grid item xs>
						<TextField
							variant="outlined"
							fullWidth
							label={<Trans>Address</Trans>}
							placeholder="rtsp://ip:port/path or YouTube URL"
							value={settings.address}
							onChange={(e) => {
								setExtractorError('');
								update('', 'address')(e);
							}}
						/>
						<Typography variant="caption">
							<Trans>Supports HTTP (HLS, DASH), RTP, RTSP, RTMP, SRT and more.</Trans>
						</Typography>
						{extractorError && (
							<Typography variant="caption" style={{ color: '#f44336', display: 'block' }}>
								{extractorError}
							</Typography>
						)}
						{extractorLoading && (
							<Typography variant="caption" style={{ color: '#2196f3', display: 'block' }}>
								⏳ Extracting stream URL… this may take up to 30s
							</Typography>
						)}
					</Grid>
					{detectedYouTubeId && (
						<Grid item>
							<Button
								variant="outlined"
								onClick={handleExtract}
								disabled={extractorLoading}
								className={classes.youtubeButton}
								title="Extract M3U8 from YouTube URL"
							>
								{extractorLoading ? <CircularProgress size={20} color="inherit" /> : <YouTubeIcon />}
							</Button>
						</Grid>
					)}
				</Grid>
			</Grid>
			{protocol === 'rtsp' && (
				<Grid item xs={12}>
					<Checkbox label={<Trans>UDP transport</Trans>} checked={settings.rtsp.udp} onChange={update('rtsp', 'udp')} />
				</Grid>
			)}
			<Grid item md={6} xs={12}>
				<TextField
					variant="outlined"
					fullWidth
					label={<Trans>Username</Trans>}
					value={settings.username}
					onChange={update('', 'username')}
					disabled={protocol === 'srt'}
				/>
				<Typography variant="caption">
					<Trans>Username for the device.</Trans>
				</Typography>
			</Grid>
			<Grid item md={6} xs={12}>
				<Password
					variant="outlined"
					fullWidth
					label={<Trans>Password</Trans>}
					value={settings.password}
					onChange={update('', 'password')}
					disabled={protocol === 'srt'}
				/>
				<Typography variant="caption">
					<Trans>Password for the device.</Trans>
				</Typography>
			</Grid>
		</React.Fragment>
	);
}

Source.defaultProps = {
	settings: {},
	config: null,
	skills: null,
	onChange: function (type, settings, inputs, ready) {},
	onYoutubeMetadata: null,
};

function SourceIcon(props) {
	return <Icon style={{ color: '#FFF' }} {...props} />;
}

const id = 'network';
const type = 'network';
const name = <Trans>Network source</Trans>;
const capabilities = ['audio', 'video'];

export { id, type, name, capabilities, SourceIcon as icon, Source as component };
